
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2, AlertCircle, Network, Cpu, Send, Check } from 'lucide-react';
import { useSparcAgent } from '@/hooks/useSparcAgent';
import { toast } from 'sonner';

interface SubAgent {
  id: string;
  name: string;
  type: string;
  status: 'idle' | 'running' | 'completed' | 'error';
  result?: string;
  error?: string;
}

const AgentSpawner: React.FC = () => {
  const [agentName, setAgentName] = useState('');
  const [agentType, setAgentType] = useState('assistant');
  const [agentInstructions, setAgentInstructions] = useState('');
  const [subAgents, setSubAgents] = useState<SubAgent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [spawningAgent, setSpawningAgent] = useState(false);
  
  const { execute, loading } = useSparcAgent();

  const handleSpawnAgent = async () => {
    if (!agentName.trim() || !agentInstructions.trim()) {
      toast.error('Please provide a name and instructions for the sub-agent');
      return;
    }

    setSpawningAgent(true);

    try {
      // Create a unique ID for the sub-agent
      const agentId = `agent-${Date.now()}`;
      
      // Add the sub-agent to the list with status 'idle'
      const newAgent: SubAgent = {
        id: agentId,
        name: agentName,
        type: agentType,
        status: 'idle'
      };
      
      setSubAgents(prev => [...prev, newAgent]);
      
      // Prepare JavaScript code to simulate spawning an agent using SPARC 2.0
      const jsCode = `
        // In a full implementation, this would use SPARC 2.0's agent spawning capabilities
        console.log("Spawning agent: ${agentName} of type ${agentType}");
        console.log("Instructions: ${agentInstructions.replace(/"/g, '\\"')}");
        
        // Simulate agent creation and setup
        const agentConfig = {
          id: "${agentId}",
          name: "${agentName}",
          type: "${agentType}",
          instructions: \`${agentInstructions.replace(/`/g, '\\`')}\`,
          processingMode: "swarm",
          parentAgentId: "primary-agent"
        };
        
        // Return a simulation of the agent creation response
        const response = {
          success: true,
          agent: agentConfig,
          message: "Agent created successfully and ready for execution"
        };
        
        response;
      `;
      
      // Execute the code using the SPARC agent
      const result = await execute(jsCode, 'javascript');
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      // Update the agent status to 'running'
      setSubAgents(prev => prev.map(agent => 
        agent.id === agentId ? { ...agent, status: 'running' } : agent
      ));
      
      // Simulate the agent execution with a delay
      setTimeout(() => {
        setSubAgents(prev => prev.map(agent => 
          agent.id === agentId ? { 
            ...agent, 
            status: 'completed',
            result: `Task completed successfully. I've analyzed ${agentInstructions.split(' ').slice(0, 3).join(' ')}... and found some interesting patterns. Would you like more details?`
          } : agent
        ));
        
        toast.success(`Agent "${agentName}" has completed its task`);
      }, 3000);
      
      // Reset form
      setAgentName('');
      setAgentInstructions('');
      
    } catch (error) {
      toast.error(`Failed to spawn agent: ${error instanceof Error ? error.message : 'Unknown error'}`);
      
      // Update the agent status to 'error' if it was added
      const failedAgent = subAgents.find(agent => agent.name === agentName);
      if (failedAgent) {
        setSubAgents(prev => prev.map(agent => 
          agent.name === agentName ? { 
            ...agent, 
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error'
          } : agent
        ));
      }
    } finally {
      setSpawningAgent(false);
    }
  };

  const getStatusBadge = (status: 'idle' | 'running' | 'completed' | 'error') => {
    switch (status) {
      case 'idle':
        return <Badge variant="outline" className="bg-gray-100">Idle</Badge>;
      case 'running':
        return <Badge variant="outline" className="bg-blue-100 text-blue-800">Running</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-100 text-green-800">Completed</Badge>;
      case 'error':
        return <Badge variant="outline" className="bg-red-100 text-red-800">Error</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Network className="mr-2 h-5 w-5 text-primary" />
          Agent Spawner
        </CardTitle>
        <CardDescription>
          Create and manage sub-agents to perform specialized tasks
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Create Sub-Agent</h3>
              <div className="space-y-2">
                <Input
                  placeholder="Agent Name"
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  disabled={spawningAgent}
                />
                
                <Select 
                  value={agentType}
                  onValueChange={setAgentType}
                  disabled={spawningAgent}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select agent type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="assistant">Assistant Agent</SelectItem>
                    <SelectItem value="researcher">Research Agent</SelectItem>
                    <SelectItem value="executor">Execution Agent</SelectItem>
                    <SelectItem value="analyzer">Analysis Agent</SelectItem>
                    <SelectItem value="coordinator">Coordination Agent</SelectItem>
                  </SelectContent>
                </Select>
                
                <Textarea
                  placeholder="Agent Instructions"
                  value={agentInstructions}
                  onChange={(e) => setAgentInstructions(e.target.value)}
                  disabled={spawningAgent}
                  rows={4}
                  className="resize-none"
                />
                
                <Button 
                  onClick={handleSpawnAgent}
                  disabled={spawningAgent || !agentName.trim() || !agentInstructions.trim()}
                  className="w-full"
                >
                  {spawningAgent ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Spawning Agent...
                    </>
                  ) : (
                    <>
                      <Cpu className="mr-2 h-4 w-4" />
                      Spawn Agent
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Active Sub-Agents</h3>
            <div className="border rounded-md">
              {subAgents.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  No sub-agents created yet
                </div>
              ) : (
                <div className="divide-y">
                  {subAgents.map((agent) => (
                    <div 
                      key={agent.id}
                      className={`p-3 cursor-pointer ${selectedAgent === agent.id ? 'bg-accent' : 'hover:bg-accent/50'}`}
                      onClick={() => setSelectedAgent(agent.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-medium">{agent.name}</div>
                        {getStatusBadge(agent.status)}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">Type: {agent.type}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {selectedAgent && (
              <div className="border rounded-md p-3">
                {(() => {
                  const agent = subAgents.find(a => a.id === selectedAgent);
                  if (!agent) return null;
                  
                  if (agent.status === 'error') {
                    return (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Error</AlertTitle>
                        <AlertDescription>{agent.error}</AlertDescription>
                      </Alert>
                    );
                  }
                  
                  if (agent.status === 'running') {
                    return (
                      <div className="flex items-center justify-center p-4">
                        <Loader2 className="h-8 w-8 animate-spin text-primary mr-2" />
                        <div>Agent is processing...</div>
                      </div>
                    );
                  }
                  
                  if (agent.status === 'completed') {
                    return (
                      <div className="space-y-2">
                        <div className="flex items-center text-green-600">
                          <Check className="h-4 w-4 mr-1" />
                          <span className="font-medium">Task completed</span>
                        </div>
                        <div className="p-2 bg-accent/50 rounded text-sm">{agent.result}</div>
                        <div className="flex justify-end">
                          <Button variant="outline" size="sm">
                            <Send className="h-3 w-3 mr-1" />
                            Send Response
                          </Button>
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="text-center text-muted-foreground p-2">
                      Agent is idle
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </CardContent>
      <CardFooter className="border-t pt-4">
        <p className="text-sm text-muted-foreground">
          SPARC 2.0 enables AI agents to create specialized sub-agents that work together to solve complex tasks.
        </p>
      </CardFooter>
    </Card>
  );
};

export default AgentSpawner;
